import {
	createStyles,
	Flex,
	getStylesRef,
	Image,
	Loader,
	UnstyledButton,
} from '@mantine/core';
import { Icon, Text } from '@repo/foundations';
import { transition } from '@repo/theme/primitives';
import type { IWorkspace } from '../../api';

interface AuthStepWorkspaceJoinRowProps {
	disabled: boolean;
	beingJoined: boolean;
	workspace: IWorkspace;
	onJoinClick: VoidFunction;
}

const useStyles = createStyles((theme) => ({
	displayOnHover: {
		ref: getStylesRef('displayOnHover'),
		opacity: 0,
		position: 'relative',
		left: `-${theme.other.space[3]}px`,
		...transition('duration-300', 'ease')('opacity'),
		...transition('duration-300', 'ease')('left'),
	},
	wrapper: {
		flexShrink: 0,
		backgroundColor: theme.other.getColor('fill/primary/default'),
		padding: theme.spacing.xs,
		borderRadius: theme.other.space[3],
		boxShadow: theme.shadows.md,
		'&:hover, &:focus': {
			backgroundColor: theme.other.getColor('fill/primary/active'),
		},
		[`&:hover .${getStylesRef('displayOnHover')}, &:focus .${getStylesRef(
			'displayOnHover'
		)}`]: {
			opacity: 1,
			left: 0,
		},
		'&:disabled': {
			opacity: 0.6,
			cursor: 'not-allowed',
		},
	},
}));

export function AuthStepWorkspaceJoinRow({
	disabled,
	beingJoined,
	workspace,
	onJoinClick,
}: AuthStepWorkspaceJoinRowProps) {
	const { classes, theme } = useStyles();
	const renderIcon = () => {
		if (beingJoined) {
			return (
				<Loader
					size="xs"
					color={theme.other.getColor('icon/primary/default')}
				/>
			);
		}
		if (!disabled) {
			return (
				<Icon
					name="arrowRight"
					color="icon/primary/default"
					className={classes.displayOnHover}
				/>
			);
		}
	};

	return (
		<UnstyledButton
			className={classes.wrapper}
			disabled={disabled}
			onClick={onJoinClick}
		>
			<Flex direction="row" justify="space-between" align="center">
				<Flex direction="row" gap="sm">
					<Image
						src={workspace.logo || '/images/logos/cfsecoda.webp'}
						mih={40}
						miw={40}
						height={40}
						width={40}
						radius="sm"
						fit="contain"
					/>
					<Flex direction="column">
						<Text
							lineClamp={1}
							size="sm"
							weight="bold"
							color="text/primary/default"
						>
							{workspace.name}
						</Text>
						<Text lineClamp={1} size="sm" color="text/secondary/default">
							{workspace.members_count} member
							{workspace.members_count > 1 && 's'}
						</Text>
					</Flex>
				</Flex>
				{renderIcon()}
			</Flex>
		</UnstyledButton>
	);
}
